import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/atoms/Alert';
import { Button } from '@/components/atoms/Buttons';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import { ToastInfo } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { useServerErrorMessage } from '@/components/organisms/FishGroupForms';
import type { DomainFishType } from '@/domain/cages';
import { useCreateSiteFishGroup } from '@/domain/fishGroups';

import type { BoardCycle, EmptyPen } from '../../types';
import ModalFormActions from '../ModalFormActions/ModalFormActions';

import NewFishGroupFields from './NewFishGroupFields';
import {
    defaultCageId,
    defaultFishTypeId,
    makeNewFishGroupDefaults,
    type NewFishGroupFormValues,
    toCreateSiteFishGroupInput,
} from './newFishGroupForm';

import styles from './NewFishGroupModal.module.scss';

interface NewFishGroupModalProps {
    clientId: number;
    locationId: number;
    /** Planned and active cycles; a customer's group must be created in one (D21). */
    cycles: BoardCycle[];
    defaultCycleId: number | null;
    pens: EmptyPen[];
    /** True until the pen list has loaded; the form waits for it instead of reporting none. */
    pensLoading?: boolean;
    /**
     * Which pens are free couldn't be checked. No pen is offered, since any could be taken,
     * and the form says so with a retry instead.
     */
    pensError?: boolean;
    onRetryPens?: () => void;
    /** Set when opened from an empty pen row. */
    defaultCageId: number | null;
    /** Real names of the site's groups, live and closed, which Suggest won't repeat. */
    existingNames?: readonly string[];
    fishTypes: DomainFishType[];
    /** The container mounts the modal per opening, so the defaults are read once, on mount. */
    isOpen: boolean;
    onClose: () => void;
    onCreated: (cycleId: number) => void;
}

export default function NewFishGroupModal(props: NewFishGroupModalProps) {
    const { clientId, locationId, cycles, defaultCycleId, pens, fishTypes } = props;
    const { defaultCageId: preferredCageId, isOpen, onClose, onCreated } = props;
    const { pensLoading = false, pensError = false, onRetryPens, existingNames = [] } = props;
    const { t } = useTranslation('fishGroups');

    const { handleSubmit, control, getValues, getFieldState, setValue, formState } =
        useForm<NewFishGroupFormValues>({
            defaultValues: makeNewFishGroupDefaults({
                cycleId: defaultCycleId,
                cageId: preferredCageId,
                pens,
                fishTypes,
            }),
        });
    const { create, error, reset: resetMutation } = useCreateSiteFishGroup(clientId, locationId);
    const serverError = useServerErrorMessage(error);

    useEffect(() => {
        const fishTypeId = defaultFishTypeId(fishTypes);
        if (getValues('fishTypeId') === '' && fishTypeId !== null) {
            setValue('fishTypeId', String(fishTypeId));
        }
    }, [fishTypes, getValues, setValue]);

    // The client overview can arrive after the modal opened; preselect a pen then, unless
    // the customer already picked one.
    useEffect(() => {
        const cageId = defaultCageId(pens, preferredCageId);
        if (getValues('cageId') === '' && !getFieldState('cageId').isDirty && cageId !== null) {
            setValue('cageId', String(cageId));
        }
    }, [pens, preferredCageId, getValues, getFieldState, setValue]);

    const close = () => {
        if (formState.isSubmitting) return;
        resetMutation();
        onClose();
    };

    const onSubmit = async (values: NewFishGroupFormValues) => {
        const input = toCreateSiteFishGroupInput(values);
        if (!input) return;
        const created = await create(input).then(
            () => true,
            () => false
        );
        if (!created) return;
        const pen = pens.find((candidate) => candidate.cageId === input.cageId)?.displayName ?? '';
        ToastInfo({
            content: t('newFishGroup.successToast', { name: input.name.trim(), pen }),
            status: 'success',
            icon: 'success',
            options: { toastId: 'site-fish-group-create-success', autoClose: 5000 },
        });
        resetMutation();
        onCreated(input.stockingCycleId);
    };

    const body = (
        <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
            <NewFishGroupFields
                control={control}
                setValue={setValue}
                cycles={cycles}
                pens={pens}
                pensLoading={pensLoading}
                pensFailed={pensError}
                existingNames={existingNames}
                fishTypes={fishTypes}
            />
            <Alert tone="neutral" size="small">
                {t('newFishGroup.note')}
            </Alert>
            {pensError && (
                <Alert
                    tone="danger"
                    size="small"
                    action={
                        <Button variant="tertiary" size="small" onClick={onRetryPens}>
                            {t('newFishGroup.retryPens')}
                        </Button>
                    }>
                    {t('newFishGroup.pensError')}
                </Alert>
            )}
            {serverError && <p className={styles.serverError}>{serverError}</p>}
            <ModalFormActions
                cancelLabel={t('actions.cancel')}
                submitLabel={t('newFishGroup.submit')}
                isSubmitting={formState.isSubmitting}
                submitDisabled={pensLoading || pensError}
                onCancel={close}
            />
        </form>
    );

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={close}
            title={t('newFishGroup.title')}
            body={body}
            backdrop="static"
        />
    );
}
